import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './pages/Login';
import AuthConfirm from './pages/AuthConfirm';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import ClientProfile from './pages/ClientProfile';
import CompanyProfile from './pages/CompanyProfile';
import UnpaidInvoices from './pages/UnpaidInvoices';
import OpenEstimates from './pages/OpenEstimates';
import Settings from './pages/Settings';
import Support from './pages/Support';
import Calendar from './pages/Calendar';
import TaskHistory from './pages/TaskHistory';
import Messages from './components/Messages';
import Navigation from './components/Navigation';
import { isAdminOrManager } from './utils/roleHelpers';
import './App.css';

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return user ? children : <Navigate to="/login" replace />;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Debug: Log user role information
  console.log('AdminRoute - User role check:', {
    user: user?.email,
    role: user?.user_metadata?.role,
    isAdminOrManager: isAdminOrManager(user),
    fullUserMetadata: user?.user_metadata
  });

  // TEMPORARY: Allow all authenticated users for testing
  // TODO: Restore role check after testing
  // Allow both admin and manager roles
  // if (!isAdminOrManager(user)) {
  //   return <Navigate to="/calendar" replace />;
  // }

  return children;
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/auth/confirm" element={<AuthConfirm />} />
          <Route
            path="/dashboard"
            element={
              <AdminRoute>
                <Navigation>
                  <Dashboard />
                </Navigation>
              </AdminRoute>
            }
          />
          <Route
            path="/clients"
            element={
              <AdminRoute>
                <Navigation>
                  <Clients />
                </Navigation>
              </AdminRoute>
            }
          />
          <Route
            path="/clients/:id"
            element={
              <AdminRoute>
                <Navigation>
                  <ClientProfile />
                </Navigation>
              </AdminRoute>
            }
          />
          <Route
            path="/companies/:id"
            element={
              <AdminRoute>
                <Navigation>
                  <CompanyProfile />
                </Navigation>
              </AdminRoute>
            }
          />
          <Route
            path="/unpaid-invoices"
            element={
              <AdminRoute>
                <Navigation>
                  <UnpaidInvoices />
                </Navigation>
              </AdminRoute>
            }
          />
          <Route
            path="/open-estimates"
            element={
              <AdminRoute>
                <Navigation>
                  <OpenEstimates />
                </Navigation>
              </AdminRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <AdminRoute>
                <Navigation>
                  <Settings />
                </Navigation>
              </AdminRoute>
            }
          />
          <Route
            path="/calendar"
            element={
              <PrivateRoute>
                <Navigation>
                  <Calendar />
                </Navigation>
              </PrivateRoute>
            }
          />
          <Route
            path="/task-history"
            element={
              <AdminRoute>
                <Navigation>
                  <TaskHistory />
                </Navigation>
              </AdminRoute>
            }
          />
          <Route
            path="/messages"
            element={
              <PrivateRoute>
                <Navigation>
                  <Messages />
                </Navigation>
              </PrivateRoute>
            }
          />
          <Route
            path="/support"
            element={
              <PrivateRoute>
                <Navigation>
                  <Support />
                </Navigation>
              </PrivateRoute>
            }
          />
          <Route 
            path="/" 
            element={
              <PrivateRoute>
                <Navigate to="/calendar" replace />
              </PrivateRoute>
            } 
          />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
